// Utilities to render a customer-specific "Worst-Case Decking Replacement"
// example inside a structure page's notes HTML.
//
// The Roof Install template's structure notes contain a seeded callout div
// tagged with `data-decking-worst-case="1"`. When we know the customer's
// actual roof square footage (via `quote.roof_total_sqft`), we swap the
// static example table for one built from *their* roof size so the ceiling
// they see is their real worst case. If no roof size is available we leave
// the seeded fallback in place.

// Pricing rules must match /app/backend/scripts/seed_decking_worst_case.py.
const SHEET_INCLUDED = 3;
const TIER_1_LIMIT = 10;
const TIER_1_PRICE = 75;
const TIER_2_PRICE = 60;
const SHEET_SQ_FT = 32; // 4' x 8' OSB
const WASTE_PCT = 0.10;

function sheetsFor(roofSqFt) {
  return Math.ceil((roofSqFt / SHEET_SQ_FT) * (1 + WASTE_PCT));
}

function costFor(totalSheets) {
  const extra = Math.max(0, totalSheets - SHEET_INCLUDED);
  const tier1 = Math.min(extra, TIER_1_LIMIT);
  const tier2 = Math.max(0, extra - TIER_1_LIMIT);
  return tier1 * TIER_1_PRICE + tier2 * TIER_2_PRICE;
}

function fmtNum(n) {
  return Math.round(n).toLocaleString("en-US");
}

function buildCustomerCallout(roofSqFt) {
  const yourSheets = sheetsFor(roofSqFt);
  const yourCost = costFor(yourSheets);
  const wasteLabel = Math.round(WASTE_PCT * 100);

  // Show two "sanity" rows so the customer sees where their roof lands on the
  // curve: a full-deck-replacement worst case (their roof) plus a "typical
  // repair" comparison (just the 3 included sheets).
  const rows = `
    <tr>
      <td style="padding:6px 10px;border:1px solid #cbd5e1;">
        Typical repair (0–3 rotten sheets)
      </td>
      <td style="padding:6px 10px;border:1px solid #cbd5e1;text-align:center;">
        Up to 3 sheets
      </td>
      <td style="padding:6px 10px;border:1px solid #cbd5e1;text-align:right;font-weight:700;color:#0F4C81;">
        $0
      </td>
    </tr>
    <tr style="background:#fef9c3;">
      <td style="padding:6px 10px;border:1px solid #cbd5e1;font-weight:700;">
        Full-deck replacement on <em>your</em> ~${fmtNum(roofSqFt)} sq ft roof
      </td>
      <td style="padding:6px 10px;border:1px solid #cbd5e1;text-align:center;font-weight:700;">
        ${yourSheets} sheets
      </td>
      <td style="padding:6px 10px;border:1px solid #cbd5e1;text-align:right;font-weight:800;color:#0F4C81;">
        ~$${fmtNum(yourCost)}
      </td>
    </tr>
  `;

  return `<div data-decking-worst-case="1"
    style="background:#fef3c7;border:2px solid #f59e0b;border-radius:10px;padding:14px 16px;margin:12px 0;text-align:left;">
    <p style="margin:0 0 6px 0;font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#92400e;font-weight:700;">
      Worst-Case Decking Replacement — Your Roof
    </p>
    <p style="margin:0 0 10px 0;font-size:13px;color:#78350f;line-height:1.5;">
      Almost every roof only needs 0–6 sheets, which is why the first three are free.
      If the entire deck were rotten and had to come off (<em>extremely rare</em>),
      here's an honest ceiling for <strong>your</strong> ~${fmtNum(roofSqFt)} sq ft roof
      using our tiered pricing (first 3 free, next 10 at $${TIER_1_PRICE}/sheet,
      then $${TIER_2_PRICE}/sheet after that) on 4×8 OSB with a ${wasteLabel}% waste factor:
    </p>
    <table style="width:100%;border-collapse:collapse;font-size:13px;color:#1e293b;background:#fffbeb;">
      <thead><tr>
        <th style="padding:6px 10px;border:1px solid #cbd5e1;background:#fde68a;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#78350f;">Scenario</th>
        <th style="padding:6px 10px;border:1px solid #cbd5e1;background:#fde68a;text-align:center;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#78350f;">Sheets needed</th>
        <th style="padding:6px 10px;border:1px solid #cbd5e1;background:#fde68a;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#78350f;">Extra cost</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <p style="margin:10px 0 0 0;font-size:11px;color:#78350f;line-height:1.4;font-style:italic;">
      Formula for your roof: (${fmtNum(roofSqFt)} sq ft ÷ ${SHEET_SQ_FT}) + ${wasteLabel}% waste,
      rounded up = ${yourSheets} sheets. First ${SHEET_INCLUDED} free, next ${TIER_1_LIMIT} at $${TIER_1_PRICE},
      remainder at $${TIER_2_PRICE}. Most projects come in at $0 additional.
    </p>
  </div>`;
}

/**
 * Given a structure page's `notes` HTML and a quote's roof_total_sqft,
 * return an HTML string with the seeded static worst-case callout replaced
 * by a customer-specific one. If no valid roof size is provided, the input
 * is returned unchanged so the seeded fallback still renders.
 */
export function applyDeckingWorstCase(notesHtml, roofSqFt) {
  if (!notesHtml || typeof notesHtml !== "string") return notesHtml;
  const sqft = Number(roofSqFt);
  if (!sqft || sqft <= 0 || !Number.isFinite(sqft)) return notesHtml;

  // Match the entire seeded callout <div data-decking-worst-case=...>...</div>.
  // The seeded div has no other nested <div>, so a non-greedy match is safe.
  const re = /<div\b[^>]*data-decking-worst-case[^>]*>[\s\S]*?<\/div>/i;
  if (!re.test(notesHtml)) return notesHtml;
  return notesHtml.replace(re, buildCustomerCallout(sqft));
}
